import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useStateContext } from "@xkit-yx/im-kit-ui";
import { Link } from "react-router-dom";
import general, { redux } from "../../../../general";

import { RedEnvelopeProvider, useRedEnvelope } from "./redEnvelope/RedEnvelopeContext";
import RedEnvelopeAlert from "./redEnvelope/RedEnvelopeAlert";

import useEmojiConfig           from "./hooks/useEmojiConfig";
import { getMessageActions, createMessageActionHandler } from "./hooks/messageActions";
import useChatComposer          from "./hooks/useChatComposer";
import useImMobileKeyboard      from "./hooks/useImMobileKeyboard";
import usePullToLoadMore        from "./hooks/usePullToLoadMore";
import useChatScrollState       from "./hooks/useChatScrollState";
import useMessageSyncOnForeground from "./hooks/useMessageSyncOnForeground";
import useTeamChatMeta          from "./hooks/useTeamChatMeta";

import MessageRow               from "./messages/MessageRow";
import AnnouncementBanner       from "./components/AnnouncementBanner";
import ServiceBar               from "./components/ServiceBar";
import ChatInputBar             from "./components/ChatInputBar";
import PackAlert                from "./components/PackAlert";
import UnreadMessagesBanner     from "./components/UnreadMessagesBanner";
import "./css/ChatMobile.css";

// ─── 内层组件：在 RedEnvelopeProvider 内，可安全调用 useRedEnvelope ──────
const IMChatMobileInner = observer(function IMChatMobileInner(props) {
  const { store, nim } = useStateContext();
  const sessionId = `team-${props.query.tid}`;
  const msgs      = store.msgStore.msgs.get(sessionId) || [];

  // ── 本地 UI 状态 ─────────────────────────────────────────────────────────
  // actionTarget: { msg, rect } | null
  // rect 是长按时 getBoundingClientRect() 的快照，用于 fixed 定位弹框
  const [actionTarget, setActionTarget] = useState(null);

  // ── 回复消息映射：预先构建 idClient→replyMsg，避免 TextMessage 在 render 内查 store ──
  // 对不在内存中的回复消息，通过 getMsgByIdServerActive 从服务端补全（参考 UIKit 源码实现）
  const [replyMsgsMap, setReplyMsgsMap] = useState({});

  // wrapper：MessageRow 调用 setActionTarget(msg, rect) 时使用
  const handleSetActionTarget = useCallback((msg, rect = null) => {
    setActionTarget(msg ? { msg, rect } : null);
  }, []);
  const [replyMsg,     setReplyMsg]     = useState(null);
  const [showEmoji,    setShowEmoji]    = useState(false);
  const hasInitedRef = useRef(false);

  // ── 回复消息映射构建（参考 UIKit teamChatContainer.tsx 894-939 行）────────
  // 遍历消息列表，对带 yxReplyMsg 的消息查找被回复消息；
  // 内存中没有的统一批量请求 getMsgByIdServerActive，避免回复预览缺失
  useEffect(() => {
    if (!msgs.length) return;
    const newMap = {};
    const reqMsgs = [];
    const idClients = [];
    msgs.forEach((msg) => {
      const rawSource = msg.ext || msg.custom;
      if (!rawSource) return;
      try {
        const parsed = typeof rawSource === 'string' ? JSON.parse(rawSource) : rawSource;
        if (!parsed?.yxReplyMsg) return;
        const yxReplyMsg = parsed.yxReplyMsg;
        const found = msgs.find((m) => m.idClient === yxReplyMsg.idClient);
        if (found) {
          newMap[msg.idClient] = found;
        } else {
          newMap[msg.idClient] = 'noFind';
          const { scene, from, to, idServer, time } = yxReplyMsg;
          if (scene && from && to && idServer && time) {
            reqMsgs.push({ scene, from, to, idServer, time });
            idClients.push(msg.idClient);
          }
        }
      } catch (e) {}
    });
    if (reqMsgs.length > 0) {
      store.msgStore.getMsgByIdServerActive({ reqMsgs })
        .then((res) => {
          res.forEach((item, idx) => { newMap[idClients[idx]] = item; });
          setReplyMsgsMap({ ...newMap });
        })
        .catch(() => setReplyMsgsMap({ ...newMap }));
    } else {
      setReplyMsgsMap({ ...newMap });
    }
  // eslint-disable-next-line
  }, [msgs]);

  // ── +1 快捷发送（末尾两条相同文本）──────────────────────────────────────
  const plusOneTextMsg = useMemo(() => {
    const textMsgs = msgs.filter(m => m.type === 'text' && m.body);
    if (textMsgs.length < 2) return null;
    const last       = textMsgs[textMsgs.length - 1];
    const secondLast = textMsgs[textMsgs.length - 2];
    if (last.body !== secondLast.body) return null;
    const lastIdx       = msgs.findIndex(m => m.idClient === last.idClient);
    const secondLastIdx = msgs.findIndex(m => m.idClient === secondLast.idClient);
    const between = msgs.slice(secondLastIdx + 1, lastIdx);
    if (!between.every(m => m.type === 'tip' || m.type === 'notification')) return null;
    return last;
  }, [msgs]);

  // ── Hooks ────────────────────────────────────────────────────────────────
  const { getEmojiConfig } = useEmojiConfig();

  const scroll = useChatScrollState({ msgs, active: props.active, sessionId, showEmoji, hasInitedRef });

  const keyboard = useImMobileKeyboard({
    active: props.active,
    listRef: scroll.listRef,
    scrollToBottom: scroll.scrollToBottom,
    isRestoringScrollRef: scroll.isRestoringScrollRef,
  });

  const composer = useChatComposer({
    store,
    sessionId,
    tid: props.query.tid,
    listRef: scroll.listRef,
    shouldScrollBottom: scroll.shouldScrollBottom,
    scrollToBottom: scroll.scrollToBottom,
    setReplyMsg,
    active: props.active,
  });

  const pullLoad = usePullToLoadMore({
    store,
    sessionId,
    listRef: scroll.listRef,
    shouldScrollBottom: scroll.shouldScrollBottom,
  });

  const meta = useTeamChatMeta({ store, nim, tid: props.query.tid, sessionId });

  const onMessageAction = useCallback(
    createMessageActionHandler({
      store,
      sessionId,
      tid: props.query.tid,
      setReplyMsg,
    }),
    // eslint-disable-next-line
    [store, sessionId, props.query.tid]
  );

  const getActions = useCallback(
    (msg) => getMessageActions(msg, { isAppointed: meta.isAppointed }),
    [meta.isAppointed]
  );

  const { handleAction } = useRedEnvelope();

  const handlePlusOne = useCallback(() => {
    if (!plusOneTextMsg?.body) return;
    scroll.shouldScrollBottom.current = true;
    store.msgStore
      .sendTextMsgActive({ scene: 'team', to: props.query.tid, body: plusOneTextMsg.body })
      .catch(() => {});
  }, [plusOneTextMsg, store, props.query.tid, scroll.shouldScrollBottom]);

  // ── 可见性同步 ────────────────────────────────────────────────────────────
  useMessageSyncOnForeground({
    active: props.active,
    updateMsg: meta.updateMsg,
    isAtBottomRef: scroll.isAtBottomRef,
    shouldScrollBottom: scroll.shouldScrollBottom,
    listRef: scroll.listRef,
  });

  // ── Redux 订阅（红包顶部消息更新）────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = meta.subscribeRedux();
    return () => { if (typeof unsubscribe === "function") unsubscribe(); };
  }, [meta.subscribeRedux]);

  // ── 导航栏操作按钮 ────────────────────────────────────────────────────────
  useEffect(() => {
    if (props.active) {
      props.setOperate(
        <div>
          <Link to={`/announcement?tid=${props.query.tid}`}>
            <img src="resources/im/gonggao.png" style={{ width: "20px" }} />
          </Link>
          <Link to={`/teamSetting?tid=${props.query.tid}`}>
            <img src="resources/im/setting.png" style={{ width: "20px", marginLeft: "8px" }} />
          </Link>
        </div>
      );
    }
  }, [props.active]);

  useEffect(() => { props.setTitle(meta.title); }, [meta.title]);

  // ── 礼包码到达时弹弹窗 ────────────────────────────────────────────────────
  useEffect(() => {
    if (meta.packSerialcode.length > 0) meta.setPackAlert(true);
  }, [meta.packSerialcode]);

  // ── 页面激活/离开生命周期 ─────────────────────────────────────────────────
  useEffect(() => {
    if (props.active) {
      const isFirst = !hasInitedRef.current;
      hasInitedRef.current = true;

      meta.gotoChat(isFirst);

      if (isFirst) {
        // ── 首次进入：保留 store 缓存（若有），后台刷新最新消息 ─────────────
        // 不主动 delete：缓存消息第一帧即可见，无「空列表→100ms 后加载完再跳」
        // needsFreshLoad（切换会话）分支仍会清空，保证跨会话数据正确
        pullLoad.resetHasMore();
        scroll.resetScrollForFreshLoad();
        meta.updateMsg();
        meta.initOnce();
      } else {
        // ── 普通返回：按需清空重载，恢复滚动位置 ──────────────────────────
        const needsFreshLoad = store.uiStore.selectedSession !== sessionId;
        if (needsFreshLoad) {
          try {
            const msgsMap = store.msgStore.msgs;
            if (msgsMap && typeof msgsMap.delete === 'function') msgsMap.delete(sessionId);
          } catch (e) {}
          pullLoad.resetHasMore();
          scroll.resetScrollForFreshLoad();
        } else if (scroll.wasAtBottomRef.current) {
          scroll.shouldScrollBottom.current = true;
        }
        meta.updateMsg();
        meta.getTeamInfo();
        meta.getRedenvelopeTopMess();
      }
    } else if (hasInitedRef.current) {
      scroll.shouldScrollBottom.current = false;
      scroll.isAtBottomRef.current      = false;
      scroll.initScrollDoneRef.current  = true;
      setShowEmoji(false);
      document.documentElement.style.setProperty('--imc-emoji-height', '0px');
    }
  }, [props.active]);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="im-mobile-chat" ref={keyboard.chatRootRef}>
        {/* ── 长按操作菜单（渲染在 .im-mobile-chat 最顶层，避免被 imc-msg-list
            的 -webkit-overflow-scrolling:touch 产生的 stacking context 遮挡）── */}
        {actionTarget && (
          <div
            className="imc-action-mask"
            onClick={() => setActionTarget(null)}
          />
        )}
        {actionTarget?.rect && (() => {
          const { msg: atMsg, rect } = actionTarget;
          const isSelf = atMsg.flow === 'out';
          // 预估菜单高度（每个操作项约 44px，最多 5 项 + 内边距）
          const MENU_HEIGHT_EST = 200;
          // 消息上方空间不足时改为显示在消息下方
          const showBelow = rect.top < MENU_HEIGHT_EST;
          const verticalStyle = showBelow
            ? { top: rect.bottom + 8 }
            : { bottom: window.innerHeight - rect.top + 8 };
          return (
            <div
              className={`imc-action-popup${isSelf ? ' imc-action-popup-self' : ' imc-action-popup-other'}${showBelow ? ' imc-action-popup-above' : ''}`}
              style={{
                position: 'fixed',
                zIndex: 1000,
                ...verticalStyle,
                ...(isSelf
                  ? { right: window.innerWidth - rect.right }
                  : { left: rect.left }),
              }}
            >
              <div className="imc-action-menu">
                {getActions(atMsg).map((action) => (
                  <button
                    key={action.key}
                    className="imc-action-item"
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setActionTarget(null);
                      onMessageAction(action.key, atMsg);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActionTarget(null);
                      onMessageAction(action.key, atMsg);
                    }}
                  >
                    {action.text}
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        <AnnouncementBanner announcement={meta.announcement} tid={props.query.tid} />

        {/* 消息列表 */}
        <div
          className="imc-msg-list"
          ref={scroll.listRef}
          onScroll={(e) => {
            if (scroll.isRestoringScrollRef.current) return;
            const el = e.currentTarget;
            const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            const atBottom = distFromBottom < 60;
            scroll.isAtBottomRef.current      = atBottom;
            scroll.shouldScrollBottom.current = atBottom;
            scroll.savedScrollTopRef.current  = el.scrollTop;
            if (atBottom) scroll.setUnreadCount(0);
          }}
        >
          {pullLoad.hasMore && (
            <div
              ref={pullLoad.pullIndicatorRef}
              className="imc-pull-indicator"
              style={{ height: pullLoad.loadingMore ? 44 : 0 }}
            >
              <div className="imc-pull-inner"><span className="imc-pull-spinner" /></div>
            </div>
          )}

          {/* listInnerRef：消息内容 wrapper，供 useChatScrollState 的 ResizeObserver 观测，
              图片/视频加载后此 div 高度增长，触发滚底补偿 */}
          <div ref={scroll.listInnerRef}>
            {(() => {
              // 在 map 外统一捕获，保证同一渲染帧内所有行拿到相同引用，
              // 让 React.memo 对 nickMembers 的浅比较能命中
              const currentNickMembers = meta.nickMembers();
              return msgs.map((msg, index) => (
                <MessageRow
                  key={msg.idClient || index}
                  msg={msg}
                  prevMsgTime={index > 0 ? msgs[index - 1].time : null}
                  isPlusOne={plusOneTextMsg?.idClient === msg.idClient}
                  setActionTarget={handleSetActionTarget}
                  onMessageAction={onMessageAction}
                  handlePlusOne={handlePlusOne}
                  insertMention={composer.insertMention}
                  nickMembers={currentNickMembers}
                  findAccountFilter={meta.findAccountFilterByAccount}
                  replyMsgsMap={replyMsgsMap}
                  getEmojiConfig={getEmojiConfig}
                />
              ));
            })()}

            <div ref={scroll.emojiSpacerRef} className="imc-emoji-spacer" />
          </div>
        </div>

        <UnreadMessagesBanner
          count={scroll.unreadCount}
          onDismiss={() => { scroll.scrollToBottom(true); scroll.setUnreadCount(0); }}
        />

        <ServiceBar gameid={meta.gameid} />

        <ChatInputBar
          replyMsg={replyMsg}
          onCancelReply={() => {
            setReplyMsg(null);
            store.msgStore.removeReplyMsgActive(sessionId);
          }}
          showEmoji={showEmoji}
          onToggleEmoji={() => {
            setShowEmoji((v) => !v);
            if (!showEmoji) composer.inputRef.current?.blur();
          }}
          inputRef={composer.inputRef}
          inputValue={composer.inputValue}
          onInputChange={composer.handleInputChange}
          onFocus={() => { setShowEmoji(false); keyboard.showKeyboard(); }}
          onBlur={keyboard.hideKeyboard}
          onKeyDown={composer.handleInputKeyDown}
          onMediaSelect={composer.handleMediaSelect}
          onSend={composer.handleSend}
          getEmojiConfig={getEmojiConfig}
          insertAtCursor={composer.insertAtCursor}
          isMuted={meta.isMuted}
        />

        <PackAlert
          visible={meta.packAlert}
          code={meta.packSerialcode}
          onClose={() => meta.setPackAlert(false)}
        />

        <RedEnvelopeAlert
          visible={meta.visible}
          type="cluster_task_accomplish"
          goBack
          getContainer={document.querySelector(".im-mobile-chat")}
          attach={meta.redenvelopeTopMsg}
          handleAction={handleAction}
        />
      </div>
  );
});

// ─── 外层容器：仅提供 RedEnvelopeProvider 上下文 ─────────────────────────
const IMChatMobile = observer(function IMChatMobile(props) {
  return (
    <RedEnvelopeProvider>
      <IMChatMobileInner {...props} />
    </RedEnvelopeProvider>
  );
});

export default IMChatMobile;
